import { _, _any } from "i18n";
import { lazy, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { SelectValue } from "ui-kit/src/MultipleSelect/types";
import { useGameStore } from "../../store";
import {
	GPEventResponse,
	GPGamePlayerResponse,
	GamePageResponse,
	ProcessingLevelEnum,
	UserWidgetSettingsResponse,
	type saveUserWidgetSettingsRequestBody,
} from "../../types";
import { getTaggedGameTeam } from "../../utils/teamTagged";
import InsightContent from "./InsightContent";
import InsightHeader from "./InsightHeader";
import ScrollToTopButton from "./ScrollToTopButton";
import { CUSTOM_GROUP_KEY, CustomTab, GameProviderTagEnum, InsightEventGroupEnum, WidgetItem, filterWidgetsForTeamTagged } from "./constants";
import { useTeamInfo } from "./contexts/DataInsightContext";
import "./css/InsightPage.css";
import { isDefaultCategory } from "./functions";
import { useEventGrouping } from "./hooks/useEventGrouping";
import { useFilterState } from "./hooks/useFilterState";
import { useWidgetManagement } from "./hooks/useWidgetManagement";
import { useInsightTabStore } from "./insight-tab-store";

// Lazy load modals
const WidgetModal = lazy(() => import("./WidgetModal"));
const AddTabModal = lazy(() => import("./AddTabModal"));
const EditTabModal = lazy(() => import("./EditTabModal"));

interface InsightPageProps {
	gameData: GamePageResponse;
	handleSaveWidgetSettings?: (insightDisplaySettings: saveUserWidgetSettingsRequestBody) => Promise<void>;
	events?: GPEventResponse[];
	toastMessage: (type: string, message: string) => void;
	userWidgetSettingsData?: UserWidgetSettingsResponse | null;
}

const InsightPage = ({ gameData, handleSaveWidgetSettings, events, toastMessage, userWidgetSettingsData }: InsightPageProps) => {
	const { eventFilter } = useGameStore();
	const { setTeamInfo } = useTeamInfo();
	// Get tab state from Zustand store - tabs are completely independent from content
	const activeCategoryTab = useInsightTabStore((state) => state.activeCategoryTab);
	const setActiveCategoryTab = useInsightTabStore((state) => state.setActiveCategoryTab);
	const [showPlayerNumber, setShowPlayerNumber] = useState(false);
	const [showWidgetModal, setShowWidgetModal] = useState(false);
	const [showAddTabModal, setShowAddTabModal] = useState(false);
	const [showEditTabModal, setShowEditTabModal] = useState(false);
	const [triggerSaveView, setTriggerSaveView] = useState(false);
	const [activeWidgetView, setActiveWidgetView] = useState<SelectValue | undefined>(undefined);
	const [editTab, setEditTab] = useState<CustomTab | null>(null);
	const [categoryTabs, setCategoryTabs] = useState<any[]>([]);
	const [selectedDropdownCategory, setSelectedDropdownCategory] = useState<SelectValue | undefined>(undefined);

	const teamTagged = gameData?.game?.teamTagged ?? null;

	const widgetManagement = useWidgetManagement({
		userWidgetSettingsData,
		handleSaveWidgetSettings,
		toastMessage,
	});

	const {
		activeWidgets,
		setActiveWidgets,
		customWidgets,
		setCustomWidgets,
		userCustomTabs,
		setUserCustomTabs,
		customTabs,
		setCustomTabs,
		isSavingView,
		updateWidgetView,
		setDefaultWidgets,
		handleDeleteWidget,
		handleMapTypeChange,
		handleSaveView,
	} = widgetManagement;

	const visibleWidgets = useMemo(() => filterWidgetsForTeamTagged(activeWidgets, teamTagged), [activeWidgets, teamTagged]);

	const filterState = useFilterState({
		gameData,
		defaultTeamId: getTaggedGameTeam(gameData)?.id ?? gameData?.homeGameTeam?.id ?? null,
	});

	const {
		periods,
		filters,
		team,
		selectedPlayer,
		handlePlayerChange,
		playerListByTeam,
		periodTabs,
		memoizedFilters,
		handlePeriodsChange,
		handleTeamChange,
		isPending,
	} = filterState;

	const deferredActiveCategoryTab = useDeferredValue(activeCategoryTab);
	const isContentPending = deferredActiveCategoryTab !== activeCategoryTab;

	const { allGroupedEvents, getEventsForCategory, activeCategoryEvents, activeGroupedEvents } = useEventGrouping({
		events,
		eventFilter,
		periods,
		team,
		selectedPlayer,
		activeCategoryTab: deferredActiveCategoryTab,
		teamTagged,
	});

	const sentinelRef = useRef<HTMLDivElement>(null);
	const filterContainerRef = useRef<HTMLDivElement>(null);
	const widgetRefs = useRef<Record<string, HTMLDivElement | null>>({});

	const isLimitedVisualization =
		gameData?.game?.providerTag?.toUpperCase() === GameProviderTagEnum.UBITRACK ||
		gameData?.game?.processingLevel === ProcessingLevelEnum.Limited;

	const allPlayers = useMemo<Record<number, GPGamePlayerResponse>>(() => {
		if (!gameData?.game) return {};

		const players: Record<number, GPGamePlayerResponse> = {};
		gameData.homeGameTeam.players.forEach((player: any) => {
			players[player.id] = player;
		});
		gameData.awayGameTeam.players.forEach((player: any) => {
			players[player.id] = player;
		});
		return players;
	}, [gameData]);

	const defaultCategoryTabs = useMemo(
		() => [
			{ key: InsightEventGroupEnum.Offensive, label: _("game.stats.menu.offensive") },
			{ key: InsightEventGroupEnum.Defensive, label: _("game.stats.menu.defensive") },
			{ key: InsightEventGroupEnum.Distribution, label: _("game.stats.menu.distribution") },
			{ key: InsightEventGroupEnum.Goalkeeper, label: _("game.stats.menu.goalkeeper") },
		],
		[]
	);

	const mergedCategoryTabs = useMemo(() => {
		return Array.from(new Map([...defaultCategoryTabs, ...userCustomTabs, ...customTabs].map((tab) => [tab.key, tab])).values());
	}, [defaultCategoryTabs, customTabs, userCustomTabs]);

	// Don't filter here - let WidgetList handle filtering with deferred value for smooth transitions
	const activeTabWidgets = useMemo(() => {
		return visibleWidgets.filter((widget) => !widget.isPlaceholder);
	}, [visibleWidgets]);

	const currentEventItems = useMemo(() => {
		return visibleWidgets
			.filter((widget: WidgetItem) => widget.group === activeCategoryTab && !widget.isPlaceholder)
			.map((widget: WidgetItem) => ({
				value: widget.key,
				label: _any(`gamePage.dataInsights.eventType.${widget.eventType}`) || `Widget ${widget.key}`,
			}));
	}, [visibleWidgets, activeCategoryTab]);

	const handleDropdownCategoryChange = useCallback(
		(selected: SelectValue) => {
			if (selected?.value !== undefined) {
				// Store update is immediate - no need for flushSync
				setActiveCategoryTab(selected?.value as InsightEventGroupEnum);
				setSelectedDropdownCategory(selected);
			}
		},
		[setActiveCategoryTab]
	);

	const handleResetDefault = useCallback(() => {
		setDefaultWidgets(true);
		toastMessage("success", _("gamePage.dataInsights.flashBag.resetMessage"));
	}, [setDefaultWidgets, toastMessage]);

	const handleWidgetRef = useCallback((key: string, el: HTMLDivElement | null) => {
		widgetRefs.current[key] = el;
	}, []);

	const showPlayerNumberSwitch = useCallback(() => {
		setShowPlayerNumber((prev) => !prev);
	}, []);

	const handleEditTab = useCallback(() => {
		const selectedCategoryTab = categoryTabs.find((tab: CustomTab) => tab.key === activeCategoryTab);
		setEditTab(selectedCategoryTab);
	}, [categoryTabs, activeCategoryTab]);

	const handleNavigateToWidget = useCallback((selected: SelectValue | null) => {
		setActiveWidgetView(selected);
		if (!selected?.value) return;

		const widgetKey = selected.value;

		const element = widgetRefs.current[widgetKey];
		if (!element) return;

		const topOffset = 250;

		// Discover uses an inner scroll container, while Portal/Admin use window scrolling.
		const scrollContainer = element.closest('[data-discover-scroll="true"]') as HTMLElement | null;

		if (scrollContainer) {
			const containerRect = scrollContainer.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();
			const offsetPosition = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - topOffset;

			scrollContainer.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			});
		} else {
			const elementPosition = element.getBoundingClientRect().top + window.scrollY;
			const offsetPosition = elementPosition - topOffset;

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			});
		}
	}, []);

	useEffect(() => {
		setTeamInfo({
			homeTeam: {
				id: gameData.homeGameTeam.id,
				shirtColor: gameData.homeGameTeam.colorShirt,
				clubLogo: gameData.homeGameTeam.logo,
			},
			awayTeam: {
				id: gameData.awayGameTeam.id,
				shirtColor: gameData.awayGameTeam.colorShirt,
				clubLogo: gameData.awayGameTeam.logo,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setCategoryTabs(mergedCategoryTabs);
	}, [mergedCategoryTabs]);

	useEffect(() => {
		if (userWidgetSettingsData?.insightDisplaySettings) {
			widgetManagement.setUserWidgetSettings(userWidgetSettingsData);
			widgetManagement.updateWidgetView(userWidgetSettingsData);
		} else {
			widgetManagement.setDefaultWidgets();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userWidgetSettingsData]);

	useEffect(() => {
		if (triggerSaveView) {
			handleSaveView();
			setTriggerSaveView(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerSaveView, handleSaveView]);

	useEffect(() => {
		if (editTab) {
			setShowEditTabModal(true);
		}
	}, [editTab]);

	useEffect(() => {
		if (activeCategoryTab) {
			// Reset widget view when tab changes
			setActiveWidgetView(undefined);

			const selectedCategoryTab = categoryTabs.find((tab: CustomTab) => tab.key === activeCategoryTab);
			if (isDefaultCategory(selectedCategoryTab?.key as InsightEventGroupEnum)) {
				setSelectedDropdownCategory(undefined);
			}
		}

		// Only reset to Offensive when the selected tab was a custom tab that no longer exists (e.g. was deleted)
		const isCustomTabSelected = activeCategoryTab?.includes(CUSTOM_GROUP_KEY);
		const selectedTabStillExists = categoryTabs.some((tab: CustomTab) => tab.key === activeCategoryTab);
		if (isCustomTabSelected && !selectedTabStillExists) {
			setActiveCategoryTab(InsightEventGroupEnum.Offensive);
		}
	}, [activeCategoryTab, categoryTabs, setActiveCategoryTab]);

	return (
		<div className="insight-page-container">
			<InsightHeader
				gameData={gameData}
				categoryTabs={categoryTabs}
				customTabs={customTabs}
				userCustomTabs={userCustomTabs}
				selectedDropdownCategory={selectedDropdownCategory}
				onDropdownCategoryChange={handleDropdownCategoryChange}
				onEditTab={handleEditTab}
				onAddTab={() => setShowAddTabModal(true)}
				team={team}
				onTeamChange={handleTeamChange}
				selectedPlayer={selectedPlayer}
				onPlayerChange={handlePlayerChange}
				playerListByTeam={playerListByTeam}
				periodTabs={periodTabs}
				periods={periods}
				onPeriodsChange={handlePeriodsChange}
				showPlayerNumber={showPlayerNumber}
				onShowPlayerNumberChange={showPlayerNumberSwitch}
				sentinelRef={sentinelRef}
				filterContainerRef={filterContainerRef}
			/>

			<InsightContent
				activeTabWidgets={activeTabWidgets}
				activeCategoryTab={deferredActiveCategoryTab}
				activeGroupedEvents={activeGroupedEvents}
				activeCategoryEvents={activeCategoryEvents}
				periods={periods}
				filters={filters}
				allPlayers={allPlayers}
				showPlayerNumber={showPlayerNumber}
				isLimitedVisualization={isLimitedVisualization}
				isPending={isPending}
				isContentPending={isContentPending}
				handleDeleteWidget={handleDeleteWidget}
				handleMapTypeChange={handleMapTypeChange}
				getEventsForCategory={getEventsForCategory}
				onWidgetRef={handleWidgetRef}
				currentEventItems={currentEventItems}
				activeWidgetView={activeWidgetView}
				onNavigateToWidget={handleNavigateToWidget}
				onAddWidget={() => setShowWidgetModal(true)}
				onResetDefault={handleResetDefault}
				onSaveView={handleSaveView}
				isSavingView={isSavingView}
			/>
			{showWidgetModal && (
				<WidgetModal
					show={showWidgetModal}
					close={() => setShowWidgetModal(false)}
					groupedEvents={allGroupedEvents || {}}
					setCustomWidgets={setCustomWidgets}
					customTabs={customTabs}
					onAddWidget={(addedWidget: WidgetItem) => {
						const newWidget = {
							...addedWidget,
							label: selectedDropdownCategory?.label || "",
						};
						setActiveWidgets((currentActiveWidgets: WidgetItem[]) => [...currentActiveWidgets, newWidget]);
					}}
					isLimitedVisualization={isLimitedVisualization}
					toastMessage={toastMessage}
				/>
			)}
			{showAddTabModal && (
				<AddTabModal
					show={showAddTabModal}
					close={() => setShowAddTabModal(false)}
					setCustomTabs={setCustomTabs}
					categoryTabs={categoryTabs}
					setTriggerSaveView={setTriggerSaveView}
					onAddedTab={(newTab) => {
						setActiveCategoryTab(newTab.key as InsightEventGroupEnum);
						const addedTab: SelectValue = {
							value: newTab.key,
							label: newTab.label,
						};
						setSelectedDropdownCategory(addedTab);
					}}
					toastMessage={toastMessage}
				/>
			)}
			{showEditTabModal && (
				<EditTabModal
					show={showEditTabModal}
					close={() => {
						setEditTab(null);
						setShowEditTabModal(false);
					}}
					tab={editTab}
					customTabs={customTabs}
					userCustomTabs={userCustomTabs}
					setCustomTabs={setCustomTabs}
					setUserCustomTabs={setUserCustomTabs}
					setTriggerSaveView={setTriggerSaveView}
					setActiveWidgets={setActiveWidgets}
					setSelectedDropdownCategory={setSelectedDropdownCategory}
					onDeletedTab={() => {
						setSelectedDropdownCategory(undefined);
						setActiveCategoryTab(InsightEventGroupEnum.Offensive);
					}}
					toastMessage={toastMessage}
				/>
			)}
			<ScrollToTopButton />
		</div>
	);
};

export default InsightPage;
